/* eslint-disable no-restricted-globals */
/* eslint-disable default-case */
import moment from 'moment';
import {
  CarrierLabelResult,
  GetLabelData,
  HistoryTrackingResponse,
  InProgress,
  LabelMetaData,
  LandingPageEstimateData,
  ShipFrom,
  ShipTo,
  SoldToAddressRequest,
  SoldToState,
  TemporaryPackage,
  TempPackageData,
} from '@/features/shipment/types';
import store from '@/features/common/store';
import { UploadDocument } from '@/features/shipment/types/documentType';
import { createLabelForCarriers, getInProgressData } from '@/features/shipment/services/shipment.services';
import i18n from '@/i18n';
import eventBus from '@/features/common/eventBus';
import eventNames from '@/features/common/constants/events';
import {
  createReturnShipment,
  deleteShipmentHistory,
  editShipmentHistory,
} from '@/features/shipment-history/services/shipment-history.services';
import {
  compareFields,
  deepClone, replaceEmptyStringWithNull, showErrorAlertDialog, showWarningAlertDialog,
} from '@/features/common/helpers';
import router from '@/router';
import { defaultInProgressData, safeListedCountryCodes } from '@/features/common/raw';
import {
  emailRules, getPostalCodeRules, minCharactersRules, phoneNumberInvalidRules,
  requiredRules, contactOrCompanyRule,
} from '@/features/common/helpers/validationRules';
import { InputValidationRule, InputValidationRules } from 'vuetify';
import { UserPreferences } from '@/features/settings/types';
import { ParsedQueryData } from '@/features/query-string/helpers';
import { prepareInvoiceForDestination } from './euProductIdentifiers';

export const getBrowserMetadata = (cardLookupId?: string | null): LabelMetaData => {
  const metaData: LabelMetaData = {};

  try {
    if (cardLookupId !== undefined) {
      metaData.cardLookupId = cardLookupId;
    }

    if (typeof navigator !== 'undefined' && navigator.userAgent) {
      metaData.browserUserAgent = navigator.userAgent;
    }

    if (typeof screen !== 'undefined') {
      metaData.browserScreenHeight = screen.height;
      metaData.browserScreenWidth = screen.width;
    }
  } catch (error) {
    // empty
  }

  return metaData;
};

export const visitCreateShipmentPage = () => {
  const isShowSuccessPage = store.getters.getShowSuccessShip;

  if (isShowSuccessPage) {
    router.push({ name: 'shipment-success' });
    return;
  }

  const isCreateShipmentPage = router.currentRoute.name === 'shipment-create';
  if (isCreateShipmentPage) return;

  router.push({ name: 'shipment-create' });
};

export const isValidDateString = (dateString: string): boolean => {
  return moment(dateString, 'YYYY-MM-DD', true).isValid();
};

export const isValidTimeString = (timeString: string): boolean => {
  return moment(timeString, 'HH:mm', true).isValid();
};

export const getCountryNameFromCode = (code: string): string => {
  const countryInfo = store.getters.getCountryFromCode(code);
  if (!countryInfo) return '';
  return countryInfo.country;
};

export const getCutoffTime = (cutoffTime: string | null | undefined): string => {
  if (!cutoffTime) return i18n.t('global.notAvailable') as string;

  if (cutoffTime === '12:00 AM' || cutoffTime === '12:00 am') {
    return i18n.t('global.notAvailable') as string;
  }

  return moment(cutoffTime, 'h:mm A').format('h:mm A');
};

export const getShipToAddressInPlainText = (shipToAddress: ShipTo | null): string => {
  if (!shipToAddress || Object.values(shipToAddress).every(
    (x) => x === null || x === '' || x === undefined || x === false || x.length === 0,
  )) {
    return i18n.t('shipment.noAddressYet') as string;
  }

  const {
    addr1,
    addr2,
    addr3,
    company,
    city,
    contact,
    phone,
    postalCode,
    province,
    countryCode,
  } = shipToAddress;

  const formattedPhone = phone ? `<span data-clarity-mask="True">${phone}</span>` : phone;
  const firstRow = [company, contact, formattedPhone, addr1, addr2, addr3].filter(Boolean).join('<br />');
  const secondRow = [postalCode, province, city, countryCode].filter(Boolean).join(', ');

  return `${firstRow}<br /> ${secondRow}`;
};

export const showEditShipmentDialog = async (onSuccess: () => void): Promise<void> => {
  eventBus.$once(eventNames.CONFIRMATION_DIALOG, onSuccess);

  let shipToAddress: ShipTo | null = null;
  const inProgressData = await getInProgressData();
  if (inProgressData) {
    shipToAddress = inProgressData.toAddress;
  }

  store.commit('toggleConfirmDialog', {
    show: true,
    content: i18n.t('shipment.editShipmentPrompt', {
      shipTo: getShipToAddressInPlainText(shipToAddress),
    }) as string,
    btnText: i18n.t('buttons.ok') as string,
    title: i18n.t('alertDialog.currentlyWorkingOn') as string,
  });
};

export const refetchInProgressDataAndSave = async (): Promise<void> => {
  const inProgressData = await getInProgressData();
  store.commit('setInProgress', inProgressData);
};

export const setDefaultInprogressData = (): void => {
  const inProgressData = deepClone(defaultInProgressData) as InProgress;
  const userPreferences = store.getters.getUserPreferences;

  const {
    signatureOption: prefSignatureOption,
    reference: prefReference,
    comments: prefComments,
    defaultAddress,
    packageType,
    weightUnits,
    dimUnits,
  } = userPreferences.shippingSettings;

  const { customsSettings } = userPreferences;

  const {
    comments,
    references,
    signatureOptions,
  } = inProgressData || {};

  let fromAddress = inProgressData?.fromAddress;

  const { reasonForExport, currencyCode } = customsSettings || {};

  if (defaultAddress) {
    const { emails } = defaultAddress;
    const userInfo = store.getters.getUserInfo;
    fromAddress = {
      ...defaultAddress,
      emails: (emails && emails.length && emails[0]) ? emails : [userInfo.username],
    };
  }

  store.commit('setInProgress', {
    ...inProgressData,
    fromAddress,
    temporaryPackage: {
      ...inProgressData.temporaryPackage,
      packageType: packageType || 'MyPackage',
      weightUnits: weightUnits || 'Lbs',
      dimUnits: dimUnits || 'Inches',
    },
    comments: comments || prefComments,
    references: references && references.length ? references : [prefReference],
    signatureOptions: signatureOptions || prefSignatureOption,
    commercialInvoice: {
      ...inProgressData.commercialInvoice,
      reasonForExport: reasonForExport || '',
      currencyCode: currencyCode || '',
    },
  });
};

export const createLabel = async (
  isClearInProgress: boolean,
  metaData?: LabelMetaData,
): Promise<CarrierLabelResult | null> => {
  const inProgressData: InProgress | null = store.getters.getInProgress;

  if (!inProgressData) return null;

  let shipDateTime = new Date().toISOString();
  if (inProgressData.shipDate) {
    shipDateTime = new Date(inProgressData.shipDate).toISOString();
  }

  const getDeliveryEmails = (): string[] => {
    const emailNotifications = inProgressData.emailNotifications || [];

    return emailNotifications.map(
      (notification) => notification.email.emailAddress,
    ).filter(Boolean);
  };

  const data: GetLabelData = {
    selectedService: inProgressData.serviceLevel,
    fromAddress: inProgressData.fromAddress,
    toAddress: inProgressData.toAddress,
    packages: inProgressData.packages,
    shipDateTime,
    deliveryEmails: getDeliveryEmails(),
    billingOption: inProgressData.billing.billingType,
    documentsOnly: inProgressData.documentsOnly,
    billingAccountNumber: inProgressData.billing.account,
    ecommerce: inProgressData.ecommerce,
    references: inProgressData.references || [],
    comments: inProgressData.comments || '',
    clearInProgress: isClearInProgress,
    editTrackingNumber: inProgressData.editTrackingNumber,
    commercialInvoice: undefined,
    returnShipment: inProgressData.isReturnShipment,
    boxFree: inProgressData.isBoxFree,
    labelFree: inProgressData.isLabelFree,
  };

  const isCommercialInvoiceNeeded: boolean = store.getters.getIsCommercialInvoice;

  if (isCommercialInvoiceNeeded && inProgressData.commercialInvoice) {
    data.commercialInvoice = prepareInvoiceForDestination(
      inProgressData.commercialInvoice, inProgressData.toAddress?.countryCode,
    );
  }

  if (inProgressData.soldTo && inProgressData.soldTo.entityMatch === 'different') {
    const { address } = inProgressData.soldTo;
    const soldToRequest: SoldToAddressRequest = {
      company: address.company || undefined,
      contact: address.contact || undefined,
      phone: address.phone || undefined,
      addr1: address.addr1 || undefined,
      addr2: address.addr2 || undefined,
      addr3: address.addr3 || undefined,
      postalCode: address.postalCode || undefined,
      city: address.city || undefined,
      province: address.province || undefined,
      emails: address.emails?.length ? address.emails : undefined,
    };
    data.soldToAddress = soldToRequest;
  }

  if (metaData) {
    data.metaData = metaData;
  }

  const uploadDocuments = inProgressData.uploadDocuments || [];
  const cleanDocIds = uploadDocuments
    .filter((doc: UploadDocument) => doc.status === 'Clean')
    .map((doc: UploadDocument) => doc.documentId);

  if (cleanDocIds.length) {
    data.documentIds = cleanDocIds;
  }

  const labelResponse = await createLabelForCarriers(data);

  if (labelResponse && labelResponse.successful) {
    store.commit('setSecureshipTrackingNo', labelResponse.secureshipTrackingNo);
  }

  return labelResponse;
};

export const modifyOrReturnShipmentWithForce = async (
  secureshipTrackingNo: string,
  opType: 'modify' | 'return',
  force: 0 | 1 = 0,
): Promise<HistoryTrackingResponse | null> => {
  let editResponse: HistoryTrackingResponse | null = null;

  if (opType === 'modify') {
    editResponse = await editShipmentHistory(
      secureshipTrackingNo,
      force,
    );
  } else if (opType === 'return') {
    editResponse = await createReturnShipment(
      secureshipTrackingNo,
      force,
    );
  }

  return editResponse;
};

export const modifyOrReturnShipment = async (
  secureshipTrackingNo: string,
  opType: 'modify' | 'return',
  refetch = false,
): Promise<void> => {
  store.commit('setLoading', true);

  const editResponse = await modifyOrReturnShipmentWithForce(
    secureshipTrackingNo,
    opType,
    0,
  );

  if (editResponse && !editResponse.successful) {
    if (editResponse.errorType === 'ShipmentIsInTransitException') {
      showWarningAlertDialog(editResponse.errorMessage);
    } else {
      showErrorAlertDialog(editResponse.errorMessage);
    }
    store.commit('setLoading', false);
    return;
  }

  if (editResponse?.isInTransit && opType === 'modify') {
    showWarningAlertDialog(i18n.t(
      'shipment.inTransitShipmentPrompt',
      {
        action: i18n.t('shipment.edited'),
      },
    ) as string);
  } else if (editResponse?.isInProgressDetected) {
    await showEditShipmentDialog(async () => {
      store.commit('setLoading', true);

      await modifyOrReturnShipmentWithForce(
        secureshipTrackingNo,
        opType, 1,
      );

      if (refetch) {
        await refetchInProgressDataAndSave();
      }

      router.push({ name: 'shipment-create' });
      store.commit('setLoading', false);
    });
  } else {
    if (refetch) {
      await refetchInProgressDataAndSave();
    }

    router.push({ name: 'shipment-create' });
  }
  store.commit('setLoading', false);
};

export const deleteShipment = (
  secureshipTrackingNo: string,
  onSuccess: () => void,
  confirmDialog: {
    content: string,
    btnText: string,
  },
): void => {
  eventBus.$once(eventNames.CONFIRMATION_DIALOG, async () => {
    store.commit('setLoading', true);

    const deleteResponse = await deleteShipmentHistory(
      secureshipTrackingNo,
    );

    store.commit('setLoading', false);

    if (deleteResponse && deleteResponse.successful) {
      onSuccess();
      return;
    }

    if (deleteResponse && deleteResponse.errorType === 'ShipmentIsInTransitException') {
      showWarningAlertDialog(deleteResponse.errorMessage);
      return;
    }

    showErrorAlertDialog();
  });

  store.commit('toggleConfirmDialog', {
    show: true,
    content: confirmDialog.content,
    btnText: confirmDialog.btnText,
  });
};

export const parseValidInProgressData = (value: InProgress): InProgress => {
  const parseValidEmails = (emailValue: undefined | null | string[]): string[] => {
    if (!emailValue) return [];
    if (!emailValue.length) return [];
    if (!emailValue[0]) return [];
    return emailValue;
  };

  const parseShipDate = (date?: string) => {
    if (!date) return '';

    const shipDate = moment(date, 'M/D/YYYY h:mm:ss A');
    if (!shipDate.isValid()) return moment(date).format('YYYY-MM-DD');

    return shipDate.format('YYYY-MM-DD');
  };

  const {
    fromAddress, toAddress, temporaryPackage, shipDate,
  } = value;

  const data: InProgress = {
    ...value,
    shipDate: parseShipDate(shipDate),
    fromAddress: {
      ...fromAddress,
      emails: parseValidEmails(fromAddress.emails),
    },
    toAddress: {
      ...toAddress,
      emails: parseValidEmails(toAddress.emails),
    },
    commercialInvoice: prepareInvoiceForDestination(
      value.commercialInvoice, toAddress?.countryCode,
    ),
    uploadDocuments: value.uploadDocuments || [],
  };

  if (value.soldTo?.entityMatch === 'different' && value.soldTo.address) {
    data.soldToAddress = { ...value.soldTo.address };
  }

  if (temporaryPackage) {
    const {
      weight, width, length, height, insurance,
    } = temporaryPackage;

    data.temporaryPackage = {
      ...temporaryPackage,
      weight: weight || 0,
      width: width || 0,
      height: height || 0,
      length: length || 0,
      insurance: insurance || 0,
    };
  }

  return replaceEmptyStringWithNull(data);
};

/**
 * Check if the order is imported and the user wants to continue with the shipment
 *
 * @returns boolean - true if the order is imported
 */
export const handleImportOrderAndContinueShipment = (): boolean => {
  const inProgressData: InProgress | null = store.getters.getInProgress;
  if (!inProgressData) return false;

  const { ecommerce } = inProgressData;
  if (!ecommerce) return false;

  const { orderId, uniqueOrderId } = ecommerce;
  if (!orderId || uniqueOrderId) return false;

  const {
    shopDetails, fulfillOrder, storeNotification,
  } = ecommerce;

  if (!fulfillOrder && !storeNotification) {
    store.commit('patchInProgress', {
      key: 'ecommerce',
      value: { ...ecommerce, orderId: '' },
    });

    return false;
  }

  const fulfillOrderMsg = fulfillOrder
    ? i18n.t('shipment.whatShipment.confirmations.ecommerce.fulfillOrder')
    : '';
  const storeNotificationMsg = storeNotification
    ? i18n.t('shipment.whatShipment.confirmations.ecommerce.storeNotification')
    : '';
  const andSeparator = fulfillOrder && storeNotification ? ` ${i18n.t('global.and')} ` : '';

  store.commit('toggleConfirmDialog', {
    show: true,
    btnText: i18n.t('buttons.ok') as string,
    content: i18n.t('shipment.whatShipment.confirmations.ecommerce.content', {
      orderId,
      shopName: shopDetails.name,
      fulfillOrderMsg,
      storeNotificationMsg,
      andSeparator,
    }) as string,
    title: i18n.t('alertDialog.areYouSure'),
  });

  const setStoreUniqueOrderId = async () => {
    eventBus.$emit(eventNames.IMPORT_ORDER_FROM_STORE, true, true);
  };

  const clearImportOrderFormAndSave = async () => {
    eventBus.$emit(eventNames.CLEAR_IMPORT_ORDER_FROM);
  };

  eventBus.$once(eventNames.CONFIRMATION_DIALOG, setStoreUniqueOrderId);

  eventBus.$once(eventNames.CONFIRMATION_DIALOG_REJECTED, clearImportOrderFormAndSave);

  return true;
};

export const getAppointmentDefaultDate = (date?: string): string => {
  const currentDate = moment(date || new Date()).add(1, 'days');

  while (currentDate.day() === 0 || currentDate.day() === 6) {
    currentDate.add(1, 'days');
  }

  return currentDate.format('YYYY-MM-DD');
};

export const getAppointmentTime = (time?: string): string => {
  if (time) return moment(time, 'h:mm a').format('HH:mm');
  return moment('12:00', 'HH:mm').format('HH:mm');
};

export const validateValue = (
  rules: (string | InputValidationRule)[],
  value: unknown,
): boolean => {
  for (const rule of rules) {
    const isValid = (rule as InputValidationRule)(value);
    if (isValid !== true) return false;
  }

  return true;
};

export const getPostalCodeValidationRules = (countryCode: string): InputValidationRules => {
  if (!countryCode) return [];

  const currentCountry = store.getters.getCountryFromCode(countryCode);
  if (!currentCountry) return [];

  return getPostalCodeRules(currentCountry);
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const getShipmentValidationRules = (
  inProgressData: InProgress,
  preferences: UserPreferences,
) => {
  const {
    fromAddress, toAddress, emailNotifications,
    commercialInvoice, references,
  } = inProgressData;

  const {
    isReferenceMandatory,
  } = preferences.shippingSettings;

  const isCommercialInvoiceNeeded = store.getters.getIsCommercialInvoice;

  const rules = new Map([
    ['fromAddress', [
      new Map([
        ['company', {
          rule: [...contactOrCompanyRule(fromAddress.contact), ...minCharactersRules(2)],
          value: fromAddress.company,
        }],
        ['contact', {
          rule: [...contactOrCompanyRule(fromAddress.company), ...minCharactersRules(2)],
          value: fromAddress.contact,
        }],
        ['phone', {
          rule: [...requiredRules, ...phoneNumberInvalidRules],
          value: fromAddress.phone,
        }],
        ['email', {
          rule: [...emailRules],
          value: fromAddress.emails ? fromAddress.emails[0] : '',
        }],
        ['addr1', {
          rule: [...requiredRules],
          value: fromAddress.addr1,
        }],
        ['countryCode', {
          rule: [...requiredRules],
          value: fromAddress.countryCode,
        }],
        ['postalCode', {
          rule: [...getPostalCodeValidationRules(fromAddress.countryCode)],
          value: fromAddress.postalCode,
        }],
        ['city', {
          rule: [...requiredRules],
          value: fromAddress.city,
        }],
        ['province', {
          rule: safeListedCountryCodes.includes(fromAddress.countryCode)
            ? [...requiredRules]
            : [],
          value: fromAddress.province,
        }],
      ]),
    ]],
    ['toAddress', [
      new Map([
        ['company', {
          rule: [...contactOrCompanyRule(toAddress.contact), ...minCharactersRules(2)],
          value: toAddress.company,
        }],
        ['contact', {
          rule: [...contactOrCompanyRule(toAddress.company), ...minCharactersRules(2)],
          value: toAddress.contact,
        }],
        ['phone', {
          rule: [...requiredRules, ...phoneNumberInvalidRules],
          value: toAddress.phone,
        }],
        ['email', {
          rule: toAddress.countryCode === 'US'
            ? [...requiredRules, ...emailRules]
            : [...emailRules],
          value: toAddress.emails ? toAddress.emails[0] : '',
        }],
        ['addr1', {
          rule: [...requiredRules],
          value: toAddress.addr1,
        }],
        ['countryCode', {
          rule: [...requiredRules],
          value: toAddress.countryCode,
        }],
        ['postalCode', {
          rule: [...getPostalCodeValidationRules(toAddress.countryCode)],
          value: toAddress.postalCode,
        }],
        ['city', {
          rule: [...requiredRules],
          value: toAddress.city,
        }],
        ['province', {
          rule: safeListedCountryCodes.includes(toAddress.countryCode) ? [...requiredRules] : [],
          value: toAddress.province,
        }],
      ]),
    ]],
    ['references', {
      rule: isReferenceMandatory ? [...requiredRules] : [],
      value: references && references.length ? references : [''],
    }],
    ['emailNotifications', {
      rule: [...emailRules],
      value: emailNotifications[0] ? emailNotifications[0].email.emailAddress : '',
    }],
  ]);

  if (!isCommercialInvoiceNeeded) return rules;

  const {
    reasonForExport, generalDescriptionOfGoods, currencyCode, declarationStatement,
    customsParts,
  } = commercialInvoice;

  if (!customsParts || !customsParts.length) return rules;

  rules.set('commercialInvoice', [
    new Map([
      ['reasonForExport', {
        rule: [...requiredRules],
        value: reasonForExport,
      }],
      ['generalDescriptionOfGoods', {
        rule: [...requiredRules],
        value: generalDescriptionOfGoods,
      }],
      ['currencyCode', {
        rule: [...requiredRules],
        value: currencyCode,
      }],
      ['declarationStatement', {
        rule: [...requiredRules],
        value: declarationStatement,
      }],
    ]),
  ]);

  return rules;
};

export const validateMapRules = (rules: ReturnType<typeof getShipmentValidationRules>): {
  valid: boolean,
  key?: string,
} => {
  for (const [key, value] of rules) {
    if (value[0] instanceof Map) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      for (const [subKey, subValue] of value[0]) {
        const { rule, value: val } = subValue;
        const isValid = validateValue(rule, val);
        // console.log(`map rule - ${subKey}`, rule, 'val', val, 'valid', isValid);
        if (!isValid) return { valid: false, key };
      }
    } else {
      const { rule, value: val } = value as { rule: InputValidationRule[], value: unknown };
      const isValid = validateValue(rule, val);
      // console.log(`object rule - ${key}`, rule, 'val', val, 'valid', isValid);
      if (!isValid) return { valid: false, key };
    }
  }

  return { valid: true };
};

export const isValidInProgressData = (inProgressData: InProgress): {
  valid: boolean,
  key?: string,
} => {
  // console.log('in-progress data: ', inProgressData);
  if (!inProgressData) return { valid: false };

  const {
    fromAddress, toAddress, packages, commercialInvoice,
  } = inProgressData;

  if (!fromAddress || !toAddress) return { valid: false };
  if (!packages || !packages.length) return { valid: false };

  const isCommercialInvoiceNeeded = store.getters.getIsCommercialInvoice;
  const { customsParts } = commercialInvoice || {};

  if (isCommercialInvoiceNeeded && (!customsParts || !customsParts.length)) {
    return { valid: false, key: 'commercialInvoice' };
  }

  const userPreferences = store.getters.getUserPreferences;

  const rules = getShipmentValidationRules(inProgressData, userPreferences);

  // console.log(rules);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  return validateMapRules(rules);
};

export const validateInprogressData = async (): Promise<boolean> => {
  await new Promise((resolve) => setTimeout(resolve, 1200));

  const inProgressData = store.getters.getInProgress;

  const { valid, key } = isValidInProgressData(inProgressData);

  if (!valid && key === 'commercialInvoice') {
    router.push({
      name: 'shipment-customs',
      query: { isValid: 'false' },
    });
    return false;
  }

  if (!valid) {
    router.push({
      name: 'shipment-create',
      query: { isValid: 'false' },
    });
    return false;
  }

  await store.dispatch('getRates', { showLoading: false });
  return true;
};

export const isOwnShippingAddress = (shipFrom: ShipFrom, shipTo: ShipTo): boolean => {
  if (!shipFrom || !shipTo) return false;

  const normalize = (value: string | null | undefined): string => (value || '').trim().toLowerCase();

  return normalize(shipFrom.addr1) === normalize(shipTo.addr1)
    && normalize(shipFrom.addr2) === normalize(shipTo.addr2)
    && normalize(shipFrom.countryCode) === normalize(shipTo.countryCode)
    && normalize(shipFrom.postalCode) === normalize(shipTo.postalCode)
    && normalize(shipFrom.city) === normalize(shipTo.city)
    && normalize(shipFrom.province) === normalize(shipTo.province);
};

/**
 * Check if Sold to address has all required fields filled.
 * Returns true when entityMatch is 'same' (Ship to data used) or
 * when entityMatch is 'different' and all required fields are filled.
 */
export const isSoldToDataValid = (soldTo?: SoldToState): boolean => {
  if (!soldTo || soldTo.entityMatch === 'same') return true;

  const { address } = soldTo;
  if (!address) return false;

  const has = (v?: string): boolean => !!(v && v.trim());

  return (has(address.contact) || has(address.company))
    && has(address.phone)
    && has(address.addr1)
    && has(address.countryCode)
    && has(address.postalCode);
};

export const compareAddresses = (fromData: ShipFrom, defaultAddress: ShipFrom): boolean => {
  if (!fromData || !defaultAddress) return false;

  const fromEmail = fromData.emails?.[0]?.trim().toLowerCase() || '';
  const defaultEmail = defaultAddress.emails?.[0]?.trim().toLowerCase() || '';

  if (fromEmail !== defaultEmail) return false;

  const fieldsToCompare: (keyof ShipFrom)[] = [
    'company', 'contact', 'phone', 'addr1', 'addr2', 'addr3', 'province', 'city',
    'countryCode', 'postalCode', 'residential', 'isInside', 'isTailGate', 'isTradeShow', 'isLimitedAccess',
  ];

  return compareFields(fromData, defaultAddress, fieldsToCompare);
};

export const validateReturnsWithCanadaPost = () => {
  const inProgressData = store.getters.getInProgress;
  const userPreferences = store.getters.getUserPreferences;

  if (!inProgressData || !userPreferences) return false;

  const {
    fromAddress, toAddress, packages,
  } = inProgressData || {};
  const { defaultAddress } = userPreferences.shippingSettings || {};

  // Check if packages exists and has at least one item
  if (!packages || !packages.length) return false;

  const { weight, weightUnits, insurance } = packages[0] || {};

  const rules = [
    (weightUnits === 'Lbs' && weight <= 50) || (weightUnits === 'Kgs' && weight <= 22.7),
    insurance <= 100,
    fromAddress?.countryCode === 'CA' && toAddress?.countryCode === 'CA',
    toAddress?.postalCode === defaultAddress?.postalCode,
    packages.length === 1,
  ];

  return rules.every((rule) => rule);
};

export function parseAddress<T extends ShipFrom | ShipTo>(
  addr: LandingPageEstimateData['fromAddress' | 'toAddress'],
): T {
  return {
    company: '',
    contact: '',
    phone: '',
    addr1: addr?.addr1 ?? '',
    countryCode: addr?.countryCode,
    postalCode: addr?.postalCode,
    city: addr.city ?? '',
    province: addr?.province ?? '',
    residential: addr?.residential ?? false,
    isInside: addr?.isInside ?? false,
    isTailGate: addr?.isTailGate ?? false,
    isTradeShow: addr?.isTradeShow ?? false,
    isLimitedAccess: addr?.isLimitedAccess ?? false,
  } as T;
}

export function parseTemporaryPackage(tempPackage: TemporaryPackage): TempPackageData {
  return {
    packageType: tempPackage.packageType ?? 'MyPackage',
    weight: tempPackage.weight ?? '',
    length: tempPackage.length ?? '',
    width: tempPackage.width ?? '',
    height: tempPackage.height ?? '',
    dimUnits: tempPackage.dimUnit ?? '',
    weightUnits: tempPackage.weightUnit ?? '',
    insurance: tempPackage.insurance ?? '',
    isAdditionalHandling: tempPackage.isAdditionalHandling ?? false,
    description: tempPackage.description ?? '',
    signatureOptions: '',
  };
}

export function parseLandingPageEstimate(
  data: LandingPageEstimateData,
): Omit<ParsedQueryData, 'isDebug'> & { temporaryPackage: TempPackageData | null } {
  return {
    fromAddress: data.fromAddress ? parseAddress<ShipFrom>(data.fromAddress) : null,
    toAddress: data.toAddress ? parseAddress<ShipTo>(data.toAddress) : null,
    packages: data.packages ?? [],
    extras: {
      comments: '',
      signature: data.signatureOption || '',
      reference: '',
    },
    isDocumentsOnly: data.isDocumentsOnly ?? false,
    emailNotifications: '',
    temporaryPackage: data.temporaryPackage ? parseTemporaryPackage(data.temporaryPackage) : null,
  };
}
